import { clienteDisplay } from '@/lib/types';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, Briefcase, Zap, X, Eye, EyeOff, Download } from 'lucide-react';
import {
PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { ChartSkeleton, KpiCardSkeleton } from '@/components/Skeleton';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Contrato, Solicitante, Cota } from '@/lib/types';
import { downloadCSV } from '@/lib/export';
import { aguardandoConfirmacaoCredito, publicadasComCreditoPorContrato } from '@/lib/distribuicao';

function valoresVisiveis(): boolean {
try {
const can = (window as unknown as { __apexCanVerValores?: boolean }).__apexCanVerValores;
if (can === false) return false;
return localStorage.getItem('apex_hide_valores') !== '1';
} catch { return false; }
}

function formatBRL(n: number): string {
if (!valoresVisiveis()) return 'R$ \u25cf\u25cf\u25cf\u25cf';
return formatBRLraw(n);
}

function formatBRLraw(n: number): string {
if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace('.0', '')}M`;
if (n >= 1_000) return `R$ ${(n / 1_000).toFixed(0)}k`;
return `R$ ${n.toLocaleString('pt-BR')}`;
}

const COLORS = {
blue: '#3b82f6',
blueLight: '#60a5fa',
green: '#10b981',
orange: '#f59e0b',
purple: '#8b5cf6',
pink: '#ec4899',
red: '#ef4444',
} as const;

const FAMILIA_COLORS: Record<string, string> = {
'Conecta Folha Business': COLORS.blue,
'BRM - BUY': COLORS.purple,
'Setorial': COLORS.pink,
'Pesquisa': COLORS.green,
};

const ORIGEM_COLORS: Record<string, string> = {
'Externo (contratual)': COLORS.blue,
'Apex (UN)': COLORS.green,
'Research (interno)': COLORS.pink,
};

const STATUS_COLORS: Record<string, string> = {
'Triagem': COLORS.blueLight,
'Em produção': COLORS.orange,
'Em revisão': COLORS.purple,
'Aguardando aprovação': COLORS.pink,
'Publicada': COLORS.green,
'Arquivada': '#7d8aa5',
'Cancelada': COLORS.red,
};

const tooltipStyle = {
backgroundColor: '#0a1020',
border: '1px solid #3b82f6',
borderRadius: 8,
fontSize: 12,
color: '#e8eefc',
padding: '6px 10px',
boxShadow: '0 4px 12px rgba(0,0,0,.5)',
};
const tooltipLabelStyle = { color: '#60a5fa', fontWeight: 600, fontSize: 11 };
const tooltipItemStyle = { color: '#e8eefc' };

type DrawerKind = null | 'mrr' | 'renovacao';

export function DashComercial() {
const { user } = useAuth();
const [hideValores, setHideValores] = useState(() => {
try { return localStorage.getItem('apex_hide_valores') === '1'; } catch { return false; }
});
const podeVerValores = user?.canVerValores === true;
function toggleHide() {
setHideValores(v => {
const n = !v;
try { localStorage.setItem('apex_hide_valores', n ? '1' : '0'); } catch {}
return n;
});
}

const [drawer, setDrawer] = useState<DrawerKind>(null);

const dashQ = useQuery({
queryKey: ['dashboard', 'visao-geral'],
queryFn: () => api.dashboards.visaoGeral().then((r) => r.data),
});
const contratosQ = useQuery({ queryKey: ['contratos'], queryFn: () => api.contratos.list().then((r) => r.data) });
const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });
const cotasQ = useQuery({ queryKey: ['cotas'], queryFn: () => api.cotas.list().then((r) => r.data) });
const eventosQ = useQuery({ queryKey: ['eventos'], queryFn: () => api.eventos.list().then(r => r.data) });
const demandasQ = useQuery({ queryKey: ['demandas'], queryFn: () => api.demandas.list().then(r => r.data) });

const d = dashQ.data;
const contratos = contratosQ.data ?? [];
const solicitantes = solicitantesQ.data ?? [];
const cotas = cotasQ.data ?? [];
const solById = useMemo(() => new Map(solicitantes.map((s) => [s.id, s])), [solicitantes]);
const cotaById = useMemo(() => new Map(cotas.map((c) => [c.id, c])), [cotas]);

const vigentes = useMemo(
() => contratos.filter((c) => c.status === 'Vigente').sort((a, b) => (b.valorMensal || 0) - (a.valorMensal || 0)),
[contratos],
);
const renovacao = useMemo(
() => contratos.filter((c) => c.status === 'Renovação em curso'),
[contratos],
);

// S1 §1.3 — métricas por crédito confirmado (meta por contrato)
const demandas = demandasQ.data ?? [];
const aguardandoCredito = useMemo(() => aguardandoConfirmacaoCredito(demandas), [demandas]);
const creditosPorContrato = useMemo(() => {
  const m = publicadasComCreditoPorContrato(demandas);
  return [...m.entries()]
    .map(([contratoId, n]) => {
      const c = contratos.find((x) => x.id === contratoId);
      return { contratoId, n, nome: c?.solicitanteNome ?? c?.identificador ?? contratoId };
    })
    .sort((a, b) => b.n - a.n);
}, [demandas, contratos]);
const totalCreditosConfirmados = useMemo(
  () => creditosPorContrato.reduce((s, x) => s + x.n, 0),
  [creditosPorContrato],
);

return (
<div>
<PageHeader title="Dash Plataforma"
action={<div className="flex items-center gap-2">
{podeVerValores && (
<button type="button" onClick={toggleHide}
className={`btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5 ${hideValores ? 'bg-orange/15 border-orange/40 text-orange' : ''}`}>
{hideValores ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
{hideValores ? 'Mostrar cifras' : 'Modo apresentação'}
</button>
)}
<button type="button" onClick={() => {
  const headers = ['Cliente', 'Família', 'Cota', 'Valor mensal', 'Status', 'Vigência início', 'Vigência fim'];
  const rows = contratos.map(c => [
    c.solicitanteNome ?? '', c.cotaFamilia ?? '', c.cotaNome ?? '',
    c.valorMensal, c.status, c.vigenciaInicio ?? '', c.vigenciaFim ?? '',
  ]);
  downloadCSV(`contratos_${new Date().toISOString().slice(0,10)}.csv`, headers, rows);
}} className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5">
<Download className="w-3.5 h-3.5" /> Exportar
</button>
</div>} />
<div className="px-6 py-4 space-y-4">
{dashQ.isLoading && (
<>
<div className="grid grid-cols-4 gap-3">
{[1,2,3,4].map(i => <KpiCardSkeleton key={i} />)}
</div>
<div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
<ChartSkeleton /><ChartSkeleton /><ChartSkeleton />
</div>
</>
)}
{dashQ.error && <div className="card text-sm text-red">Erro ao carregar.</div>}
{d && (
<>
<div className="grid grid-cols-4 gap-3">
<button
type="button"
onClick={() => setDrawer('mrr')}
className="text-left hover:ring-1 hover:ring-blue/40 rounded-xl transition-shadow"
>
<KpiCard
label="Receita mensal" color="blue"
value={formatBRL(d.mrr)}
delta={`${d.contratosVigentes} contratos · ver detalhes`}
icon={<DollarSign className="w-[18px] h-[18px]" />}
/>
</button>
<KpiCard
label="Contratos ativos" color="green"
value={d.contratosVigentes}
delta={`${d.contratosTotal - d.contratosVigentes} em outras fases`}
icon={<Briefcase className="w-[18px] h-[18px]" />}
/>
<button
type="button"
onClick={() => setDrawer('renovacao')}
className="text-left hover:ring-1 hover:ring-orange/40 rounded-xl transition-shadow"
>
<KpiCard
label="Em renovação" color="orange"
value={renovacao.length}
delta={renovacao.length ? 'clique pra ver clientes' : 'nenhum em renovação'}
icon={<Briefcase className="w-[18px] h-[18px]" />}
/>
</button>
<KpiCard
label="Demandas (histórico)" color="purple"
value={d.demandasTotal}
delta="acumulado da operação"
icon={<Zap className="w-[18px] h-[18px]" />}
/>
</div>

{/* Bloco de eventos */}
<div className="card p-4 mt-4">
<div className="flex items-center justify-between mb-3">
<h3 className="text-sm font-bold">Próximos eventos</h3>
</div>
<div className="space-y-2">
{(eventosQ.data ?? [])
.filter(e => e.status !== 'Concluído' && new Date(e.dataInicio || e.data || '') >= new Date())
.sort((a, b) => new Date(a.dataInicio || a.data || '').getTime() - new Date(b.dataInicio || b.data || '').getTime())
.slice(0, 5)
.map(ev => (
<div key={ev.id} className="flex items-center gap-3 text-sm">
<span className="text-muted text-xs w-16 shrink-0">{ev.dataInicio ? new Date(ev.dataInicio).toLocaleDateString('pt-BR', {day:'2-digit',month:'2-digit'}) : '—'}</span>
<span className="flex-1 truncate">{ev.nome}</span>
<span className={`text-xs px-2 py-0.5 rounded-full ${ev.status === 'Confirmado' ? 'bg-green/15 text-green' : 'bg-blue/15 text-blue-light'}`}>{ev.status}</span>
</div>
))}
{(eventosQ.data ?? []).filter(e => e.status !== 'Concluído').length === 0 && (
<p className="text-xs text-muted">Nenhum evento próximo cadastrado.</p>
)}
</div>
</div>

{/* S1 §1.3 — créditos de distribuição (meta por contrato) */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
<div className="card p-4">
<div className="flex items-center justify-between mb-3">
<h3 className="text-sm font-bold">Entregas creditadas por contrato</h3>
<span className="text-[10px] text-muted">{totalCreditosConfirmados} crédito{totalCreditosConfirmados !== 1 ? 's' : ''} confirmado{totalCreditosConfirmados !== 1 ? 's' : ''}</span>
</div>
{creditosPorContrato.length === 0 ? (
<p className="text-xs text-muted">Nenhuma entrega publicada com crédito confirmado ainda.</p>
) : (
<div className="space-y-2">
{creditosPorContrato.slice(0, 8).map((c) => {
const max = creditosPorContrato[0]?.n ?? 1;
return (
<div key={c.contratoId} className="flex items-center gap-3">
<div className="w-40 text-xs text-text-2 truncate" title={c.nome}>{clienteDisplay(c.nome)}</div>
<div className="flex-1 h-4 bg-panel-2 rounded relative">
<div className="h-full bg-gradient-to-r from-green to-green/60 rounded" style={{ width: `${(c.n / max) * 100}%` }} />
</div>
<span className="text-xs font-bold w-6 text-right">{c.n}</span>
</div>
);
})}
</div>
)}
</div>
<div className="card p-4">
<div className="flex items-center justify-between mb-3">
<h3 className="text-sm font-bold">Créditos aguardando confirmação</h3>
<span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${aguardandoCredito.length > 0 ? 'bg-yellow/15 text-yellow' : 'bg-green/15 text-green'}`}>
{aguardandoCredito.length}
</span>
</div>
{aguardandoCredito.length === 0 ? (
<p className="text-xs text-muted">Tudo confirmado — nenhuma publicação pendente de crédito.</p>
) : (
<div className="space-y-1.5">
{aguardandoCredito.slice(0, 8).map((dem) => (
<div key={dem.id} className="flex items-center gap-2 text-xs">
<span className="w-1.5 h-1.5 rounded-full bg-yellow shrink-0" />
<span className="flex-1 truncate" title={dem.titulo}>{dem.titulo}</span>
<span className="text-[10px] text-muted shrink-0">{dem.solicitanteNome ?? ''}</span>
</div>
))}
<p className="text-[10px] text-muted pt-1">Confirme os créditos no card da demanda (Distribuição).</p>
</div>
)}
</div>
</div>

<div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
<FamiliaPie data={d.porFamilia} />
<OrigemPie data={d.porOrigem} />
<StatusBar data={d.porStatus} />
</div>

<div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
<div className="card">
<h3 className="text-sm font-bold mb-3">Top 10 clientes por volume</h3>
<div className="space-y-2">
{d.topClientes.slice(0, 10).map((c) => {
const max = d.topClientes[0]?.volume ?? 1;
return (
<div key={c.id} className="flex items-center gap-3">
<div className="w-40 text-xs text-text-2 truncate" title={c.nome}>{c.nome}</div>
<div className="flex-1 h-5 bg-panel-2 rounded relative">
<div className="h-full bg-gradient-to-r from-blue to-blue-light rounded" style={{ width: `${(c.volume / max) * 100}%` }} />
</div>
<div className="w-10 text-right text-xs font-bold">{c.volume}</div>
</div>
);
})}
</div>
</div>

<div className="card">
<h3 className="text-sm font-bold mb-3">Distribuição por área executora</h3>
<div className="space-y-2">
{Object.entries(d.porArea).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([a, n]) => {
const max = Math.max(...Object.values(d.porArea));
return (
<div key={a} className="flex items-center gap-3">
<div className="w-40 text-xs text-text-2 truncate" title={a}>{a}</div>
<div className="flex-1 h-5 bg-panel-2 rounded">
<div className="h-full bg-gradient-to-r from-purple to-pink rounded" style={{ width: `${(n / max) * 100}%` }} />
</div>
<div className="w-10 text-right text-xs font-bold">{n}</div>
</div>
);
})}
</div>
</div>
</div>
</>
)}
</div>

{drawer === 'mrr' && (
<ContratosDrawer
titulo="Contratos vigentes · MRR"
subtitulo={`${vigentes.length} contratos · ${formatBRL(vigentes.reduce((s, c) => s + (c.valorMensal || 0), 0))} de receita mensal`}
contratos={vigentes}
solById={solById}
cotaById={cotaById}
onClose={() => setDrawer(null)}
/>
)}
{drawer === 'renovacao' && (
<ContratosDrawer
titulo="Contratos em renovação"
subtitulo={`${renovacao.length} cliente${renovacao.length === 1 ? '' : 's'} no funil de renovação`}
contratos={renovacao}
solById={solById}
cotaById={cotaById}
onClose={() => setDrawer(null)}
/>
)}
</div>
);
}

function ContratosDrawer({ titulo, subtitulo, contratos, solById, cotaById, onClose }: {
titulo: string;
subtitulo: string;
contratos: Contrato[];
solById: Map<string, Solicitante>;
cotaById: Map<string, Cota>;
onClose: () => void;
}) {
const total = contratos.reduce((s, c) => s + (c.valorMensal || 0), 0);
return (
<div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
<div onClick={(e) => e.stopPropagation()}
className="bg-panel border-l border-border w-full max-w-md h-full overflow-y-auto flex flex-col">
<div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-panel z-10">
<div>
<h2 className="text-sm font-bold">{titulo}</h2>
<p className="text-[11px] text-muted mt-0.5">{subtitulo}</p>
</div>
<button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
</div>
<div className="p-4 space-y-1.5">
{contratos.length === 0 && <div className="text-xs text-muted">Nenhum contrato nessa categoria.</div>}
{contratos.map((c) => {
const sol = solById.get(c.solicitanteId);
const cota = c.cotaId ? cotaById.get(c.cotaId) : undefined;
return (
<div key={c.id} className="bg-panel-2 border border-border rounded p-2.5">
<div className="flex items-start justify-between gap-2 mb-1">
<div className="font-semibold text-text text-xs leading-snug truncate flex-1">{clienteDisplay(sol?.nome)}</div>
<div className="text-xs font-bold text-blue-light shrink-0">{formatBRL(c.valorMensal || 0)}</div>
</div>
<div className="text-[10px] text-muted">
{c.identificador && <span>{c.identificador}</span>}
{cota && <span> · {cota.familia} · {cota.hierarquia}</span>}
</div>
{(c.vigenciaInicio || c.vigenciaFim) && (
<div className="text-[10px] text-text-2 mt-1">
{c.vigenciaInicio ? new Date(c.vigenciaInicio).toLocaleDateString('pt-BR') : '—'}
{' → '}
{c.vigenciaFim ? new Date(c.vigenciaFim).toLocaleDateString('pt-BR') : '—'}
</div>
)}
</div>
);
})}
</div>
{contratos.length > 0 && (
<div className="px-4 py-3 border-t border-border bg-panel-2 sticky bottom-0">
<div className="flex items-center justify-between text-xs">
<span className="text-muted">Total</span>
<span className="font-bold text-blue-light">{formatBRL(total)}</span>
</div>
</div>
)}
</div>
</div>
);
}

interface PieEntry { name: string; value: number; color: string }

function FamiliaPie({ data }: { data: Record<string, number> }) {
const entries: PieEntry[] = Object.entries(data)
.filter(([, v]) => v > 0)
.map(([name, value]) => ({ name, value, color: FAMILIA_COLORS[name] ?? COLORS.blue }));
const total = entries.reduce((s, e) => s + e.value, 0);

return (
<div className="card">
<h3 className="text-sm font-bold mb-1">Mix por família de cota</h3>
<p className="text-xs text-muted mb-3">{total} demandas com contrato vinculado</p>
<div className="h-56">
<ResponsiveContainer width="100%" height="100%">
<PieChart>
<Pie isAnimationActive={false} data={entries} dataKey="value" nameKey="name"
cx="50%" cy="45%" innerRadius={50} outerRadius={85}
label={({ percent }) => ((percent ?? 0) * 100 | 0) > 4 ? `${(percent ?? 0) * 100 | 0}%` : ""}
labelLine={false}
>
{entries.map((e, i) => <Cell key={i} fill={e.color} stroke="#0a1020" strokeWidth={1} />)}
</Pie>
<Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
<Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11, color: '#b3bed3' }} />
</PieChart>
</ResponsiveContainer>
</div>
</div>
);
}

function OrigemPie({ data }: { data: Record<string, number> }) {
const entries: PieEntry[] = Object.entries(data)
.filter(([, v]) => v > 0)
.map(([name, value]) => ({ name, value, color: ORIGEM_COLORS[name] ?? COLORS.blue }));
const total = entries.reduce((s, e) => s + e.value, 0);

return (
<div className="card">
<h3 className="text-sm font-bold mb-1">Origem das demandas</h3>
<p className="text-xs text-muted mb-3">{total} demandas no total</p>
<div className="h-56">
<ResponsiveContainer width="100%" height="100%">
<PieChart>
<Pie isAnimationActive={false} data={entries} dataKey="value" nameKey="name"
cx="50%" cy="45%" innerRadius={50} outerRadius={85}
label={({ percent }) => ((percent ?? 0) * 100 | 0) > 4 ? `${(percent ?? 0) * 100 | 0}%` : ""}
labelLine={false}
>
{entries.map((e, i) => <Cell key={i} fill={e.color} stroke="#0a1020" strokeWidth={1} />)}
</Pie>
<Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
<Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11, color: '#b3bed3' }}
formatter={(v) => String(v).replace(' (contratual)', '').replace(' (interno)', '').replace(' (UN)', '')}
/>
</PieChart>
</ResponsiveContainer>
</div>
</div>
);
}

function StatusBar({ data }: { data: Record<string, number> }) {
const ativos = ['Triagem', 'Em produção', 'Em revisão', 'Aguardando aprovação'];
const arquivos = ['Publicada', 'Arquivada', 'Cancelada'];
const entries = ativos.map((status) => ({ status, n: data[status] ?? 0, color: STATUS_COLORS[status] ?? COLORS.blue }));
const totalAtivo = entries.reduce((s, e) => s + e.n, 0);
const totalHistorico = arquivos.reduce((s, st) => s + (data[st] ?? 0), 0);
const publicadas = data['Publicada'] ?? 0;

return (
<div className="card">
<h3 className="text-sm font-bold mb-1">Pipeline ativo</h3>
<p className="text-xs text-muted mb-3">
{totalAtivo} demanda{totalAtivo === 1 ? '' : 's'} em andamento
{totalHistorico > 0 && ` · ${publicadas} publicada${publicadas === 1 ? '' : 's'} no histórico`}
</p>
<div className="h-56">
<ResponsiveContainer width="100%" height="100%">
<BarChart data={entries} margin={{ top: 8, right: 4, left: -20, bottom: 4 }}>
<CartesianGrid stroke="#1f2d4d" vertical={false} strokeDasharray="3 3" />
<XAxis dataKey="status" tick={{ fill: '#7d8aa5', fontSize: 10 }} axisLine={false} tickLine={false} interval={0}
tickFormatter={(v) => {
const s = String(v);
if (s === 'Em produção') return 'Produção';
if (s === 'Em revisão') return 'Revisão';
if (s === 'Aguardando aprovação') return 'Aprovação';
return s;
}}
/>
<YAxis tick={{ fill: '#7d8aa5', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
<Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: 'rgba(59,130,246,0.05)' }} />
<Bar dataKey="n" radius={[4, 4, 0, 0]} isAnimationActive={false}>
{entries.map((e, i) => <Cell key={i} fill={e.color} />)}
</Bar>
</BarChart>
</ResponsiveContainer>
</div>
</div>
);
}
